import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useCreateWorkTimeEntryMutation,
  useGetWorkTimeEntriesByDateRangeQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import UserInput from '@/shared/users/components/UserInput';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {CreateWorkTimeEntryFormValues} from '../utils/workTimeFormInputs';
import {createWorkTimeEntryFormSchema} from '../utils/workTimeFormInputs';

export type AddWorkTimeEntryDialogProps = DialogProps;

function AddWorkTimeEntryDialog({
  onOpenChange,
  ...props
}: AddWorkTimeEntryDialogProps) {
  const {t} = useTranslation();
  const {toast} = useToast();
  const createEntry = useCreateWorkTimeEntryMutation(graphqlClient);
  const form = useForm<CreateWorkTimeEntryFormValues>({
    resolver: zodResolver(createWorkTimeEntryFormSchema),
    defaultValues: {userId: '', startedAt: '', endedAt: '', note: ''},
  });

  const handleClose = (open: boolean) => {
    form.reset();
    onOpenChange?.(open);
  };

  const handleSubmit = async (values: CreateWorkTimeEntryFormValues) => {
    try {
      await createEntry.mutateAsync({input: values});
      await queryClient.invalidateQueries({
        queryKey: useGetWorkTimeEntriesByDateRangeQuery.getKey(),
      });
      toast({title: t('Work time entry created'), variant: 'success'});
      handleClose(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={handleClose}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Add work time')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="flex flex-col gap-4">
              <FormRequiredLegend />

              <FormField
                control={form.control}
                name="userId"
                render={({field}) => (
                  <FormItem>
                    <FormLabel required>{t('Employee')}</FormLabel>
                    <FormControl>
                      <UserInput
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {(['startedAt', 'endedAt'] as const).map(name => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({field}) => (
                    <FormItem>
                      <FormLabel required>
                        {name === 'startedAt' ? t('Started at') : t('Ended at')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="datetime-local"
                          value={dayjs(field.value || undefined).format(
                            TIME_FORMATS.dateTimeHTMLLocal
                          )}
                          onChange={event =>
                            field.onChange(
                              dayjs(event.target.value).toISOString()
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <FormField
                control={form.control}
                name="note"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>{t('Note')}</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleClose(false)}
              >
                {t('Close')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={createEntry.isPending}
              >
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
}

export default AddWorkTimeEntryDialog;
